import { Link } from "@tanstack/react-router";
import { Instagram, Mail, MapPin, ArrowRight } from "lucide-react";
import { navLinks } from "@/lib/catalog";
import logo from "@/assets/sunset-logo.webp";
import logoAvif from "@/assets/sunset-logo.avif";

const aide = [
  { label: "Livraison", to: "/livraison" },
  { label: "Échanges & retours", to: "/echanges-retours" },
  { label: "Guide des tailles", to: "/guide-des-tailles" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
] as const;

const infos = [
  "À propos",
  "Conditions générales",
  "Politique de confidentialité",
];

const instagramUrl = "https://www.instagram.com/sunset_concepstore";

export function Footer() {
  return (
    <footer className="overflow-hidden bg-background">
      {/* Newsletter band — warm sand background for contrast and hierarchy */}
      <div className="bg-sand/40">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-20">
          <div>
            <p className="label-xs text-accent">Newsletter</p>
            <h2 className="mt-3 font-serif text-2xl font-light leading-tight tracking-tight sm:text-3xl">
              Entrez dans l'univers Sunset
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              Inscrivez-vous pour découvrir nos nouveautés, nos collections et
              les inspirations méditerranéennes de chaque saison.
            </p>
          </div>

          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-col gap-3 sm:flex-row"
          >
            <label htmlFor="footer-newsletter" className="sr-only">
              Adresse e-mail
            </label>
            <div className="relative flex-1">
              <Mail
                size={16}
                strokeWidth={1.3}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                id="footer-newsletter"
                type="email"
                required
                placeholder="Votre adresse e-mail"
                className="h-14 w-full border border-border bg-background pl-11 pr-4 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-accent"
              />
            </div>
            <button
              type="submit"
              className="btn-primary group inline-flex items-center gap-2"
            >
              S'inscrire
              <ArrowRight
                size={14}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </form>
        </div>
      </div>

      {/* Main footer */}
      <div className="mx-auto max-w-7xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Link to="/" aria-label="Sunset Concept Store — accueil">
              <picture>
                <source type="image/avif" srcSet={logoAvif} />
                <img
                  src={logo}
                  alt="Sunset Concept Store"
                  loading="lazy"
                  decoding="async"
                  className="h-16 w-auto lg:h-20"
                />
              </picture>
            </Link>
            <p className="mt-7 max-w-xs text-sm leading-[1.8] text-muted-foreground">
              Concept store féminin méditerranéen : beachwear, kimonos, sacs et
              accessoires pour vos plus beaux moments au soleil.
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm text-muted-foreground">
              <MapPin size={16} strokeWidth={1.3} className="text-accent" />
              <span>Livraison partout au Maroc</span>
            </div>
          </div>

          {/* Navigation columns */}
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-5 lg:gap-8">
            <div>
              <h3 className="label-xs text-foreground">Boutique</h3>
              <ul className="mt-6 space-y-4">
                {navLinks.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="link-underline text-sm text-muted-foreground transition-colors hover:text-foreground">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="label-xs text-foreground">Aide</h3>
              <ul className="mt-6 space-y-4">
                {aide.map((a) => (
                  <li key={a.label}>
                    <Link
                      to={a.to}
                      className="link-underline text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {a.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="label-xs text-foreground">Informations</h3>
              <ul className="mt-6 space-y-4">
                {infos.map((i) => (
                  <li key={i}>
                    <span className="link-underline cursor-pointer text-sm text-muted-foreground transition-colors hover:text-foreground">
                      {i}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Instagram column */}
          <div className="lg:col-span-3">
            <h3 className="label-xs text-foreground">Instagram</h3>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Suivez-nous pour découvrir les coulisses, les nouveautés et
              l'ambiance golden hour de Sunset.
            </p>
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-3 text-sm text-foreground transition-colors hover:text-accent"
              aria-label="Ouvrir le profil Instagram de Sunset Concept Store"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-secondary transition-colors hover:border-accent/50 hover:bg-accent/10">
                <Instagram size={18} strokeWidth={1.3} />
              </span>
              <span className="font-serif text-lg tracking-tight">@sunset_concepstore</span>
            </a>
          </div>
        </div>

        {/* Divider and bottom bar */}
        <div className="mt-16 border-t border-border/70 pt-8 lg:mt-20 lg:pt-10">
          <div className="flex flex-col items-center justify-between gap-4 text-center sm:flex-row sm:text-left">
            <p className="text-xs text-muted-foreground">
              © 2026 Sunset Concept Store. Tous droits réservés.
            </p>
            <p className="text-xs text-muted-foreground">
              Conçu avec soin pour l'été.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
